import axios from '../../axios.js'

/**
 * Audit Trail Actions
 *
 * Read-only access to the immutable audit trail persisted by
 * smartinvoice-api-core (AuditTrailService). The list endpoint is already
 * tenant-scoped server-side and, for non-admin tenant users, only returns
 * PUBLIC entries — so no tenant filtering is needed client-side.
 *
 * API Endpoints (smartinvoice-api-core AuditTrailController):
 * - GET /audit-trail                  - paginated list (filters + pagination)
 * - GET /audit-trail/stats/summary    - summary counts (24h)
 * - GET /audit-trail/trails-by-doc-no - entries for a specific document
 * - GET /audit-trail/:id/details      - full entry with change diff
 */
export default {
  /**
   * Fetch a paginated page of audit entries.
   *
   * @param {Object} payload
   * @param {number} payload.page  - 1-based page number
   * @param {number} payload.limit - page size
   * @param {Object} payload.filters - { entityType, action, identifier, startDate, endDate }
   */
  fetchRecords({ commit }, payload = {}) {
    return new Promise((resolve, reject) => {
      const { page = 1, limit = 25, filters = {} } = payload
      const offset = (page - 1) * limit

      const params = { limit, offset }
      if (filters.entityType) params.entityType = filters.entityType
      if (filters.action) params.action = filters.action
      if (filters.identifier) params.identifier = filters.identifier
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      axios
        .get('/audit-trail', { params })
        .then((response) => {
          const records = response.data?.data || []
          const pg = response.data?.pagination || {}
          const total = pg.total != null ? pg.total : records.length
          const pageCount = Math.max(1, Math.ceil(total / limit))

          commit('SET_AUDITS', records)
          commit('SET_PAGINATION', {
            currentPage: page,
            totalPage: pageCount,
            pageTotal: records.length,
            totalItem: total,
            limit,
            offset,
          })
          resolve(response)
        })
        .catch((error) => {
          console.error('[AuditTrail] Failed to fetch records:', error)
          reject(error)
        })
    })
  },

  /** Summary statistics for the header cards (last 24h + totals). */
  fetchStats({ commit }) {
    return new Promise((resolve) => {
      axios
        .get('/audit-trail/stats/summary')
        .then((response) => {
          const stats = response.data?.data || response.data || null
          commit('SET_STATS', stats)
          resolve(stats)
        })
        .catch((error) => {
          // Stats are non-critical; never block the page on them.
          console.warn('[AuditTrail] Failed to fetch stats:', error)
          commit('SET_STATS', null)
          resolve(null)
        })
    })
  },

  /**
   * Fetch all audit entries for a specific document.
   * tenantId is required by the API for this route.
   */
  fetchByDocNo(context, { docNo, tenantId, type, docType } = {}) {
    return new Promise((resolve, reject) => {
      if (!docNo || !tenantId) {
        reject(new Error('docNo and tenantId are required'))
        return
      }
      const params = { docNo, tenantId }
      if (type) params.type = type
      if (docType) params.docType = docType

      axios
        .get('/audit-trail/trails-by-doc-no', { params })
        .then((response) => resolve(response.data || []))
        .catch((error) => {
          console.error('[AuditTrail] Failed to fetch trails by docNo:', error)
          reject(error)
        })
    })
  },

  /** Full detail (with change diff + related entries) for one entry. */
  fetchDetails(context, id) {
    return new Promise((resolve, reject) => {
      if (!id) {
        reject(new Error('Audit id is required'))
        return
      }
      axios
        .get(`/audit-trail/${id}/details`)
        .then((response) => resolve(response.data))
        .catch((error) => {
          console.error(`[AuditTrail] Failed to fetch details for ${id}:`, error)
          reject(error)
        })
    })
  },
}
